/* PureHeat motion engine - vanilla JS, no libraries.
   Scroll reveals, parallax hero, ambient gold heat particles.
   All gated by prefers-reduced-motion. Idempotent. */
(function () {
  if (window.__phAnim) return;
  window.__phAnim = true;

  /* Global GSAP-ready gate. Section scripts must never read window.gsap directly:
     if a section script runs one tick before the non-defer, end-of-body GSAP
     bundle finishes parsing, a bare guard silently no-ops and the animation is
     lost with no error (this is what broke the Will It Fit draw-in when the libs
     were briefly deferred). phGsapReady resolves the moment gsap and ScrollTrigger
     both exist (now, or within a short rAF budget), registers ScrollTrigger once,
     and rejects cleanly under reduced motion so callers fall through to their
     visible base state. Cached: every caller shares one resolution. */
  (function () {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.phGsapReady = new Promise(function (resolve, reject) {
      if (reduceMotion) { reject(new Error('reduced-motion')); return; }
      var tries = 0, MAX = 120; /* about 2s at 60fps, then give up gracefully */
      (function poll() {
        if (window.gsap && window.ScrollTrigger) {
          try { window.gsap.registerPlugin(window.ScrollTrigger); } catch (e) {}
          resolve(window.gsap);
          return;
        }
        if (++tries > MAX) { reject(new Error('gsap-unavailable')); return; }
        requestAnimationFrame(poll);
      })();
    });
    window.phGsapReady.catch(function () {});
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var revealEls = document.querySelectorAll('.ph-reveal, .ph-reveal-left, .ph-section-wipe');

    if (reduce) {
      revealEls.forEach(function (el) { el.classList.add('ph-revealed'); });
      return;
    }

    /* 1. Scroll-triggered reveals.
       A fixed threshold + rootMargin makes everything reveal at the same late,
       uniform point regardless of size: a small card and a full-bleed hero both
       wait until they clear the same 40px lip. Large (hero-scale) elements need
       to start moving slightly earlier so they glide into their resting position
       as you scroll, instead of snapping in after they're already past. This
       pairs with the slow --ph-ease-reveal deceleration and eased Lenis scroll.
       Two observers: small elements keep the deliberate later trigger; large
       elements fire on first contact with the viewport extended below the fold. */
    if ('IntersectionObserver' in window) {
      var revealOne = function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = parseInt(el.dataset.delay || 0, 10);
          setTimeout(function () { el.classList.add('ph-revealed'); }, delay);
          observer.unobserve(el);
        });
      };
      // Small / normal elements: reveal once meaningfully in view, past a 40px lip.
      var obsSmall = new IntersectionObserver(revealOne, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      // Large elements: trigger on first contact, with the root grown 12% below the
      // fold so the reveal begins earlier and eases into place as it scrolls up.
      var obsLarge = new IntersectionObserver(revealOne, { threshold: 0, rootMargin: '0px 0px 12% 0px' });
      var vh = window.innerHeight || document.documentElement.clientHeight || 800;
      revealEls.forEach(function (el) {
        var h = el.getBoundingClientRect().height || el.offsetHeight || 0;
        (h >= vh * 0.6 ? obsLarge : obsSmall).observe(el);
      });
    } else {
      revealEls.forEach(function (el) { el.classList.add('ph-revealed'); });
    }

    /* 2. Parallax hero */
    var hero = document.getElementById('ph-hero');
    var heroBg = document.getElementById('ph-hero-bg');
    if (hero && heroBg) {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          var y = window.scrollY;
          if (y < hero.offsetHeight * 1.5) { heroBg.style.transform = 'translateY(' + (y * 0.4) + 'px)'; }
          ticking = false;
        });
      }, { passive: true });
    }

    /* 3. Ambient gold heat particles */
    initHeatParticles();

    window.__phAnimReady = true;

    /* 4. Magnetic pull on hooked controls. Delegated on document so any control
       carrying the [data-ph-magnetic] hook (and every .ph-btn) gets the effect,
       including buttons injected after load (cart drawer, predictive search, the
       Will It Fit picker) with no re-scan. Fine-pointer + hover devices only,
       transform-only (no layout cost), skipped under reduced motion. The base
       :hover lift in base.css still applies; this adds the follow toward the
       cursor. data-ph-no-magnetic (on the control or an ancestor) opts out, so
       partner/embedded buttons keep their own behaviour. Same MAG/CAP as before. */
    if (!reduce && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var MAG = 0.18, CAP = 6;
      var MAG_SEL = '[data-ph-magnetic], .ph-btn';
      var magTarget = function (node) {
        if (!node || !node.closest) return null;
        var el = node.closest(MAG_SEL);
        if (!el || el.closest('[data-ph-no-magnetic]')) return null;
        return el;
      };
      document.addEventListener('mousemove', function (e) {
        var btn = magTarget(e.target);
        if (!btn) return;
        var r = btn.getBoundingClientRect();
        var tx = Math.max(-CAP, Math.min(CAP, (e.clientX - (r.left + r.width / 2)) * MAG));
        var ty = Math.max(-CAP, Math.min(CAP, (e.clientY - (r.top + r.height / 2)) * MAG));
        btn.style.transform = 'translate(' + tx + 'px,' + ty + 'px)';
      }, { passive: true });
      /* mouseout bubbles (mouseleave does not), so it can be delegated; ignore
         moves onto a child of the same control, matching the old mouseleave. */
      document.addEventListener('mouseout', function (e) {
        var btn = magTarget(e.target);
        if (!btn) return;
        if (e.relatedTarget && btn.contains(e.relatedTarget)) return;
        btn.style.transition = 'transform 0.4s var(--ph-ease, ease)';
        btn.style.transform = '';
        setTimeout(function () { btn.style.transition = ''; }, 400);
      });
    }
  });

  function initHeatParticles() {
    var canvas = document.getElementById('ph-particles');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');

    function resize() { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    var COUNT = 25;
    var particles = [];
    for (var i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: canvas.height + Math.random() * canvas.height,
        r: 2 + Math.random() * 3,
        speedY: 0.4 + Math.random() * 0.6,
        drift: (Math.random() - 0.5) * 0.3,
        opacity: 0.05 + Math.random() * 0.12,
        phase: Math.random() * Math.PI * 2
      });
    }

    var inView = false;
    var frame = 0;
    function schedule() {
      if (inView && !document.hidden && !frame) frame = requestAnimationFrame(draw);
      else if ((!inView || document.hidden) && frame) { cancelAnimationFrame(frame); frame = 0; }
    }
    function draw() {
      frame = 0;
      if (!inView || document.hidden) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      var now = Date.now() / 1000;
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += Math.sin(now * 0.5 + p.phase) * 0.4 + p.drift;
        p.y -= p.speedY;
        var progress = 1 - p.y / canvas.height;
        var fade = Math.sin(progress * Math.PI);
        if (fade < 0) { fade = 0; }
        var alpha = p.opacity * fade;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(201,168,106,' + alpha + ')';
        ctx.fill();
        if (p.y < -10) { p.y = canvas.height + 10; p.x = Math.random() * canvas.width; }
      }
      frame = requestAnimationFrame(draw);
    }
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        schedule();
      }, { rootMargin: '100px' });
      observer.observe(canvas);
    } else {
      function checkView() {
        var box = canvas.getBoundingClientRect();
        inView = box.bottom > -100 && box.top < window.innerHeight + 100;
        schedule();
      }
      window.addEventListener('scroll', checkView, { passive: true });
      window.addEventListener('resize', checkView, { passive: true });
      checkView();
    }
    document.addEventListener('visibilitychange', schedule);
  }
})();
